import type { RouteWithGeometry, Waypoint } from '$lib/components/map/types';
import type { SearchParams } from '$lib/services/urls/searchParams';
import type { ItiAddress, WaypointsInfo } from '$routes/(map)/routes/types';
import type { PoisStoreContent } from '$routes/(map)/types';
import type { Page } from '@sveltejs/kit';
import type { AdId } from './types';
import {
	isCorridorPageContext,
	isRouteResultsPage,
	isRouteSearchPageContext,
	isZoiPageContext,
} from '../urls/routeIds';

interface GetRawTargetingArgs {
	poisStore?: PoisStoreContent;
	searchParams: SearchParams;
	route?: RouteWithGeometry;
	// Itinerary waypoints coming from the itinerary results
	waypoints?: Waypoint[];
	departureAddress?: ItiAddress;
	arrivalAddress?: ItiAddress;
	// Itinerary waypoints coming from the search system when the user searches for an itinerary
	searchWaypoints?: WaypointsInfo;
	page: Page;
	adId: AdId;
}

function getRawTargeting({
	poisStore,
	searchParams,
	route,
	waypoints,
	arrivalAddress,
	departureAddress,
	searchWaypoints,
	page,
	adId,
}: GetRawTargetingArgs): Record<string, string | undefined | boolean | number> {
	const departureSearchWaypoint = searchWaypoints?.departure;
	const arrivalSearchWaypoint = searchWaypoints?.arrival;
	const departureWaypoint = waypoints?.[0];
	const arrivalWaypoint = waypoints?.[waypoints.length - 1];

	const itiResultsDepartmentTo
		= arrivalAddress?.country?.code === 'FRA' ? arrivalAddress?.zipCode?.substring(0, 2) : undefined;
	const itiResultsDepartmentFrom
		= departureAddress?.country?.code === 'FRA'
			? departureAddress?.zipCode?.substring(0, 2)
			: undefined;

	const searchResultsDepartmentTo
		= arrivalSearchWaypoint?.countryCode === 'FRA'
			? arrivalSearchWaypoint?.zipCode?.substring(0, 2)
			: undefined;
	const searchResultsDepartmentFrom
		= departureSearchWaypoint?.countryCode === 'FRA'
			? departureSearchWaypoint?.zipCode?.substring(0, 2)
			: undefined;

	if (shouldShowItineraryTargeting(page, adId)) {
		return {
			avoidborders: searchParams.tripConstraint === 'NO_BORDER_CROSSING',
			avoidtoll: searchParams.tripConstraint === 'NO_TOLL',
			itinerarydistance: route?.tracking.distanceType,
			itihighwaydistance: route?.tracking.highwayDistance,
			ititotaldistance: route?.tracking.distance,
			fuelcost: route?.costs.fuel.amount, // Check that always in orange
			fuelcostunit: route?.costs.fuel.currency, // Check with Orange this new key
			fuelprice: searchParams.energyPrice,
			fuelpriceunit: searchParams.currency, // Check with Orange this new key
			carbtype: searchParams.car?.energy,
			highway: Boolean(route && route.highwayDistance.value > 0),
			itinerarytype: route?.tracking.type,
			vehicle: searchParams.travelMode,
			areafrom: departureAddress?.department ?? undefined,
			areato: arrivalAddress?.department ?? undefined,
			countryto: arrivalAddress?.country?.code ?? arrivalSearchWaypoint?.countryCode ?? undefined,
			countryfrom:
				departureAddress?.country?.code ?? departureSearchWaypoint?.countryCode ?? undefined,
			citynameto: arrivalAddress?.city ?? undefined,
			citynamefrom: departureAddress?.city ?? undefined,
			regionto: arrivalAddress?.region ?? arrivalSearchWaypoint?.region ?? undefined,
			regionfrom: departureAddress?.region ?? departureSearchWaypoint?.region ?? undefined,
			deptto: itiResultsDepartmentTo ?? searchResultsDepartmentTo,
			deptfrom: itiResultsDepartmentFrom ?? searchResultsDepartmentFrom,
			postalcodefrom: departureAddress?.zipCode ?? undefined,
			postalcodeto: arrivalAddress?.zipCode ?? undefined,
			latto: arrivalWaypoint?.location.lat ?? undefined,
			lonto: arrivalWaypoint?.location.lng ?? undefined,
			latfrom: departureWaypoint?.location.lat ?? undefined,
			lonfrom: departureWaypoint?.location.lng ?? undefined,
			carbrand: searchParams.car?.maker,
			carmodel: searchParams.car?.model,
			carfuel: searchParams.car?.energy,
		};
	}

	// The selected address is accessible through the PoiStore because it can come from the search
	// params or the url (SEO mode)
	const selectedAddress = poisStore?.selectedAddress;
	return {
		lat: selectedAddress?.coordinates?.lat,
		lon: selectedAddress?.coordinates?.lng,
		postalcode: selectedAddress?.zipCode,
		region: selectedAddress?.region,
		country: selectedAddress?.countryCode,
		city: selectedAddress?.city,
	};
}

export function getTargeting(args: GetRawTargetingArgs) {
	const rawTargeting = getRawTargeting(args);

	return Object.fromEntries(
		Object.entries(rawTargeting).filter(([, value]) => value !== undefined && value !== null),
	);
}

function shouldShowItineraryTargeting(page: Page, adId: AdId) {
	return (
		isCorridorPageContext(page.route)
		|| isRouteResultsPage(page.route)
		|| isRouteSearchPageContext(page.route)
		|| isZoiPageContext(page.route)
		|| adId === 'itinerary-results-loading'
		|| adId.startsWith('tracking-iti-result')
	);
}
